import React, { useEffect, useState } from 'react';
import { shareLinkIn, fetchUnfurl, stripShareLink } from './chatUnfurl';

/**
 * LinkPreview — the card under a room message that shares a result.
 *
 * share-link-unfurl slug. Deliberately the SAME shape as a `/tasks` row —
 * question left, thumbnail right — because that is what the thing being linked
 * looks like everywhere else in the app, and a preview that invents its own
 * layout makes the reader work out twice what they are looking at.
 *
 * The thumbnail is the server-rendered `preview.png`, not a live card. In a
 * `/tasks` row the thumbnail IS a live card, scaled — but a room can hold
 * dozens of messages, and mounting a (possibly sandboxed, possibly heavy) card
 * per link would cost an iframe each. An <img> is one request and cannot run
 * anything.
 */

// Matches ROW_THUMB_W/H in ScheduledTasks.js. Same object, same proportions.
const THUMB_W = 132;
const THUMB_H = 76;

// `compact` (a one-to-one station chat) shows only a "View result" link; a room keeps the card.
export function PreviewCard({ preview, onNavigate, onOpenShared, openHint, compact = false }) {
  const open = (e) => {
    // A modified click opens a new tab — nothing is being unloaded, so the
    // leave-station confirmation would be noise. Same rule as
    // ScheduledRunLink.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    // The caller opens it in the output panel, so the chat stays on screen and nothing
    // unloads; that is why this is not a plain link.
    if (onOpenShared) {
      e.preventDefault();
      onOpenShared(preview);
      return;
    }
    if (!onNavigate) return;
    e.preventDefault();
    onNavigate(preview.url);
  };
  // Where it will open depends on the station's shape, which only the
  // caller knows — a tooltip that guesses is worse than none.
  const title = onOpenShared ? (openHint || 'Open this result') : undefined;
  if (compact) {
    return (
      <a href={preview.url} title={title} onClick={open} data-testid="link-preview"
         style={{ display: 'inline-block', marginTop: 4, fontSize: '0.85rem', fontWeight: 600,
                  color: '#2563eb', textDecoration: 'none' }}>
        View result ›
      </a>
    );
  }
  return (
    <a
      href={preview.url}
      title={title}
      onClick={open}
      data-testid="link-preview"
      style={{
        display: 'flex', alignItems: 'center', gap: 12, marginTop: 4,
        padding: 8, borderRadius: 10, maxWidth: 380,
        border: '1px solid #e2e8f0', background: '#ffffff',
        textDecoration: 'none', color: 'inherit',
      }}
    >
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          overflow: 'hidden', fontSize: '0.82rem', lineHeight: 1.35, color: '#334155',
        }}>
          {/* A record with no stored question still gets a card — it has a
              thumbnail, which is the half that identifies it. */}
          {preview.title || (preview.live ? 'Shared card' : 'Shared result')}
        </span>
        {/* A live card has no thumbnail; it runs when opened, so say so. */}
        {preview.live && (
          <span style={{ display: 'block', marginTop: 3, fontSize: '0.68rem', color: '#94a3b8' }}>
            Live card · opens with current data
          </span>
        )}
        {/* Only when there is something to count — the rule ScheduledRunLink
            already states: "1 card" on every single-card result is noise. It
            also stopped a titleless card from reading "Shared result" twice,
            once as the fallback title and once here. */}
        {preview.cards > 1 && (
          <span style={{ display: 'block', marginTop: 3, fontSize: '0.68rem', color: '#94a3b8' }}>
            {preview.cards} cards
          </span>
        )}
      </span>
      {preview.image && (
        <img
          src={preview.image}
          alt=""
          width={THUMB_W}
          height={THUMB_H}
          loading="lazy"
          style={{
            flex: '0 0 auto', width: THUMB_W, height: THUMB_H,
            objectFit: 'cover', objectPosition: 'top',
            borderRadius: 6, border: '1px solid #f1f5f9', background: '#f8fafc',
          }}
        />
      )}
    </a>
  );
}

/**
 * Resolve the share link in `text` (if any).
 *
 * Returns `{ preview, text }` — the resolved preview (or null), and the text
 * the MESSAGE should now show, which is the original minus the URL once a card
 * is standing in for it. The two have to come from one place: the bubble can
 * only drop the URL if it knows a card replaced it, and only this hook knows
 * that.
 *
 * Renders nothing at all while resolving — no skeleton, no spinner. A room is a
 * live surface and messages arrive under the one being read; a placeholder that
 * later grows into a card shifts everything below it twice instead of once.
 */
export function useLinkPreview(text) {
  const [preview, setPreview] = useState(null);
  const [settled, setSettled] = useState(null);      // the url whose fetch has finished
  const url = shareLinkIn(text);

  useEffect(() => {
    if (!url) { setPreview(null); return undefined; }
    let live = true;
    setSettled(null);
    // Drop a response whose request is no longer the current one.
    //
    // NOT about unmount warnings — React 18 removed that warning, so a guard
    // written for it cannot be shown to do anything (this one was, and its test
    // passed against its own removal until the claim was checked). The race it
    // DOES prevent is ordering: the list reuses a component when history is
    // trimmed or a message is replaced, so `url` changes under an in-flight
    // fetch. Without this, a slow response for the OLD url resolves last and
    // overwrites the new preview — the wrong card under the right message.
    fetchUnfurl(url).then((p) => { if (live) { setPreview(p); setSettled(url); } });
    return () => { live = false; };
  }, [url]);

  // autoroute-continuity-pin: callers that must not show a raw link (a chat card link) need
  // to tell "still loading" from "failed".
  let status = 'none';
  if (url) status = settled !== url ? 'loading' : (preview ? 'ready' : 'failed');
  return {
    preview: url ? preview : null,
    text: url && preview ? stripShareLink(text, url) : text,
    url,
    status,
  };
}

/** The card on its own, for callers that render the message text themselves. */
export default function LinkPreview({ text, onNavigate, onOpenShared, openHint }) {
  const { preview } = useLinkPreview(text);
  if (!preview) return null;
  return <PreviewCard preview={preview} onNavigate={onNavigate}
                      onOpenShared={onOpenShared} openHint={openHint} />;
}
